import { encodeGraphQLResponse, splitEncoding } from '@contentful/live-preview';

import type { Country, Locale } from '@/types/regionTypes';

import type { ContentfulLanguageCodeEnum } from '@/components/CatalogDownloadModal/components/CatalogDownloadForm/helper';
import {
  APOLLO_CLIENTS,
  clearContentfulExtensions,
  CustomContext,
  getLastContentfulExtensions,
  initializeApollo,
} from '@/graphql/client';
import type {
  ContentfulDefaultHeaderSettingsQuery,
  ContentfulDefaultHeaderSettingsQueryVariables,
  ContentfulDefaultLayout,
  ContentfulDefaultLayoutQuery,
  ContentfulDefaultLayoutQueryVariables,
  ContentfulDetailPageQuery,
  ContentfulDetailPageQueryVariables,
  ContentfulDictionaryItem,
  ContentfulDictionaryQuery,
  ContentfulDictionaryQueryVariables,
  ContentfulFixedPageQuery,
  ContentfulFixedPageQueryVariables,
  ContentfulGetAllNewsPagesQuery,
  ContentfulGetAllNewsPagesQueryVariables,
  ContentfulListingPageQuery,
  ContentfulListingPageQueryVariables,
  ContentfulPage,
  ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQuery,
  ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables,
  ContentfulPageBreadcrumbFragment,
  ContentfulPageExhibitionHistoriesFaqQuery,
  ContentfulPageExhibitionHistoriesFaqQueryVariables,
  ContentfulPageQuery,
  ContentfulPageQueryVariables,
  ContentfulPlaceholderServiceCollectionByTypeQuery,
  ContentfulPlaceholderServiceCollectionByTypeQueryVariables,
  ContentfulSelectorModalByMarketingPartnerQuery,
  ContentfulSelectorModalByMarketingPartnerQueryVariables,
  ContentfulSeoConfiguration,
  ContentfulSysFragment,
} from '@/graphql/contentful/api';
import {
  type ContentfulPageBoutiqueListCollectionQuery,
  type ContentfulPageBoutiqueListCollectionQueryVariables,
  DefaultHeaderSettingsDocument,
  DefaultLayoutDocument,
  DetailPageDocument,
  DictionaryDocument,
  FixedPageDocument,
  GetAllNewsPagesDocument,
  ListingPageDocument,
  PageBoutiqueDetailCollectionBySlugAndTrackingKeyDocument,
  PageBoutiqueListCollectionQueryDocument,
  PageDocument,
  PageExhibitionHistoriesFaqDocument,
  PlaceholderServiceCollectionByTypeDocument,
  SelectorModalByMarketingPartnerDocument,
} from '@/graphql/contentful/api';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { cleanPreviewMachineStrings } from '@/integrations/contentful/cleanPreviewEncoding';
import {
  fetchContentfulExtraData,
  handleDuplicateBreadcrumbs,
} from '@/integrations/contentful/helpers';
import type {
  ContentfulData,
  ContentfulFixedPageCMSData,
} from '@/integrations/contentful/types';
import type { CMSLayoutDataProps } from '@/layouts/Default';
import { BoutiqueDetailCMSTypeEnum } from '@/lib/boutique/helpers';
import {
  getBrandConfig,
  LocaleConfiguration,
} from '@/lib/configuration/configuration';
import { captureError, captureMessage } from '@/lib/errors';
import type { LocaleMessages } from '@/lib/intl';
import { fetchSaleorProductSkus } from '@/lib/productSkus';
import { SEO_HREFLANG_COUNTRIES } from '@/lib/seo/Config';
import { makeHrefLangs } from '@/lib/seo/Functions';
import { SeoConfigBuilder } from '@/lib/seo/SeoVariables';
import type { Maybe } from '@/lib/types';
import { translateStrapMaterial } from '@/views/StrapMaterialView/helpers';

export const HOME_SLUG = '/';

export type DetailPageType = 'watch' | 'strap' | 'eyewear';

const encodePreviewResponse = <T,>(
  data: T,
  isPreview?: boolean | null,
  extensions?: Record<string, unknown>,
): T => {
  if (isPreview && extensions) {
    const response = encodeGraphQLResponse({ data, extensions });
    return cleanPreviewMachineStrings(response.data) as T;
  }
  return data;
};

export const filterContentfulCollection = (items: unknown[] | undefined) => {
  const allItems = items;

  return (allItems?.filter((item) => {
    return !!(item as Maybe<{ sys: ContentfulSysFragment }>)?.sys?.id;
  }) || []) as ContentfulData[];
};

function setHreflangsFromLocalization(
  seoConfigBuilder: SeoConfigBuilder | null | undefined,
  localizationConfiguration: unknown,
  localeOrLanguage: string,
) {
  try {
    const supportedCountries =
      LocaleConfiguration.getInstance().getSupportedCountries();
    const currentLanguage = (localeOrLanguage || '').split('-')[0];
    const hreflangCandidates = makeHrefLangs(
      supportedCountries,
      SEO_HREFLANG_COUNTRIES,
      currentLanguage,
    );
    const activeCountries = Array.isArray(localizationConfiguration)
      ? (localizationConfiguration as string[])
      : [];
    const activatedHreflangs = hreflangCandidates.filter((code) => {
      const country = code.split('-')[1]?.toLowerCase();
      return country && activeCountries.includes(country);
    });
    if (activatedHreflangs?.length) {
      seoConfigBuilder?.setHreflangs(activatedHreflangs);
    }
  } catch (e) {
    if (e instanceof Error) {
      captureError(e);
    } else {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }
}

export const fetchCMSPlaceholderServiceData = async (
  type: string,
  languageCode: string,
) => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulPlaceholderServiceCollectionByTypeQuery,
    ContentfulPlaceholderServiceCollectionByTypeQueryVariables
  >({
    query: PlaceholderServiceCollectionByTypeDocument,
    variables: {
      type,
      languageCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  return data.placeholderServiceCollection?.items?.[0];
};

export const getBreadcrumbSlug = (slug: string): Array<string> => {
  const pageList = slug.split('/');
  const slugArr: Array<string> = [];
  let path = '';

  for (let i = 0; i < pageList.length; i++) {
    const page = pageList[i];
    if (i === 0) {
      path = page;
    } else {
      path = `${path}/${pageList[i]}`;
    }
    slugArr.push(path);
  }

  //add home as root
  if (!slugArr?.includes(HOME_SLUG)) {
    slugArr.unshift(HOME_SLUG);
  }

  return slugArr;
};

export const sortBreadcrumb = (
  breadcrumbList: ContentfulPageBreadcrumbFragment[],
): ContentfulPageBreadcrumbFragment[] => {
  const home = breadcrumbList.find(function (item) {
    return item.slug === HOME_SLUG;
  });
  const arr = breadcrumbList
    .filter(function (item) {
      return item.slug !== HOME_SLUG;
    })
    .sort(function (a, b) {
      return (a.slug?.length || 0) > (b.slug?.length || 0) ? 1 : -1;
    });
  if (home) {
    arr.unshift(home);
  }
  return arr;
};

export const fetchCMSPageData = async (
  slug: string,
  languageCode: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
  type?: PageType | null,
) => {
  const client = initializeApollo();
  const parentSlugs = getBreadcrumbSlug(slug);

  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulPageQuery,
    ContentfulPageQueryVariables
  >({
    query: PageDocument,
    variables: {
      slug,
      languageCode,
      parentSlugs,
      countryCode,
      preview: isPreview,
      type: type ?? null,
    },
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.contentful,
    }),
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const cmsData = filterContentfulCollection(
    data.pageCollection?.items?.[0]?.contentCollection?.items,
  );

  const pageCollectionItems = data.pageCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);

  const mainHeader = data.pageCollection?.items?.[0]?.mainHeader;
  const breadcrumb = handleDuplicateBreadcrumbs(data.breadcrumb?.items);

  const pageTitle = pageCollectionItems?.pageTitle;
  const shortDescription = pageCollectionItems?.shortDescription;
  const pageType = pageCollectionItems?.type;
  const tagsCollection = pageCollectionItems?.tagsCollection;
  const publishDate = pageCollectionItems?.publishDate;
  const simpleFooter = data?.pageCollection?.items?.[0]?.simpleFooter;
  const footer = data?.pageCollection?.items?.[0]?.footer;
  const localizationConfiguration = (pageCollectionItems as any)
    ?.localizationConfiguration;

  return {
    cmsData,
    mainHeader,
    breadcrumb,
    seoConfigBuilder,
    pageTitle,
    shortDescription,
    pageType,
    tagsCollection,
    publishDate,
    simpleFooter,
    footer,
    localizationConfiguration,
  };
};

export const fetchCMSPageExhibitionHistoriesFaqData = async (
  slug: string,
  languageCode: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
  type?: PageType | null,
) => {
  const client = initializeApollo();
  const parentSlugs = getBreadcrumbSlug(slug);

  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulPageExhibitionHistoriesFaqQuery,
    ContentfulPageExhibitionHistoriesFaqQueryVariables
  >({
    query: PageExhibitionHistoriesFaqDocument,
    variables: {
      slug,
      languageCode,
      parentSlugs,
      countryCode,
      preview: isPreview,
      type: type ?? null,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const cmsData = filterContentfulCollection(
    data.pageExhibitionHistoriesFaqCollection?.items?.[0]?.contentCollection
      ?.items,
  );

  const pageCollectionItems =
    data.pageExhibitionHistoriesFaqCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);
  const localizationConfiguration = (pageCollectionItems as any)
    ?.localizationConfiguration;

  const mainHeader =
    data.pageExhibitionHistoriesFaqCollection?.items?.[0]?.mainHeader;
  const previousBreadcrumbItems = handleDuplicateBreadcrumbs(
    data.breadcrumb?.items,
  );
  const currentPage = data.pageExhibitionHistoriesFaqCollection?.items[0];
  const breadcrumb = [
    ...previousBreadcrumbItems,
    {
      slug: currentPage?.slug,
      pageTitle: currentPage?.pageTitle,
      sys: currentPage?.sys ?? { id: '' },
    },
  ];

  setHreflangsFromLocalization(
    seoConfigBuilder,
    localizationConfiguration,
    languageCode,
  );

  return {
    cmsData,
    mainHeader,
    breadcrumb,
    seoConfigBuilder,
    localizationConfiguration,
  };
};

export const fetchCMSDefaultLayoutData = async (
  locale: Locale,
  country: Country,
  disableErrorNotification?: boolean,
  isPreview?: boolean | null | undefined,
) => {
  const client = initializeApollo();
  const contentfulLocale = locale.contentful ?? locale.locale;

  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulDefaultLayoutQuery,
    ContentfulDefaultLayoutQueryVariables
  >({
    query: DefaultLayoutDocument,
    variables: {
      languageCode: contentfulLocale,
      countryCode: country.slug,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
      disableErrorNotification,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  return data.defaultLayoutCollection?.items[0];
};
export const fetchCmsPageDataWithExtraData = async (
  slug: string,
  locale: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
) => {
  try {
    const type = getTypeByPageSlug(slug);

    const dataByPage = await fetchCMSPageData(
      slug,
      locale,
      countryCode,
      isPreview,
      type,
    );

    let cmsDataGroup = dataByPage;
    if (dataByPage.cmsData.length === 0) {
      const dataByPageExhibitionHistoriesFaqData =
        await fetchCMSPageExhibitionHistoriesFaqData(
          slug,
          locale,
          countryCode,
          isPreview,
          type,
        );
      if (dataByPageExhibitionHistoriesFaqData.cmsData.length > 0) {
        cmsDataGroup = {
          cmsData: dataByPageExhibitionHistoriesFaqData.cmsData,
          mainHeader: dataByPageExhibitionHistoriesFaqData.mainHeader,
          breadcrumb: dataByPageExhibitionHistoriesFaqData.breadcrumb,
          seoConfigBuilder:
            dataByPageExhibitionHistoriesFaqData.seoConfigBuilder,
          pageTitle: '',
          shortDescription: '',
          publishDate: '',
          pageType: null,
          tagsCollection: { items: [] },
          simpleFooter: {},
          footer: null,
          localizationConfiguration: undefined,
        };
      }
    }

    const {
      breadcrumb,
      cmsData,
      footer,
      localizationConfiguration,
      mainHeader,
      pageTitle,
      pageType,
      publishDate,
      seoConfigBuilder,
      shortDescription,
      simpleFooter,
      tagsCollection,
    } = cmsDataGroup;

    if (cmsData.length === 0) {
      return {
        cmsData: [],
        cmsExtraData: {},
        cmsDefaultLayoutData:
          mainHeader || simpleFooter || footer
            ? {
                ...(mainHeader && { header: mainHeader }),
                ...(simpleFooter && { footer: simpleFooter }),
                ...(footer && { footer }),
              }
            : undefined,
      };
    }
    let cmsExtraData = {};
    if (!getBrandConfig().disableSaleorApiCall) {
      cmsExtraData = await fetchContentfulExtraData(
        cmsData,
        languageCode,
        channel,
        isPreview ?? undefined,
      );
    }
    const { saleorProductSkus, saleorProductSkusProductVariant } =
      await fetchSaleorProductSkus(cmsData, languageCode, channel);

    // Derive hreflangs from localizationConfiguration
    setHreflangsFromLocalization(
      seoConfigBuilder,
      localizationConfiguration,
      locale,
    );

    return {
      cmsData: cmsData,
      cmsExtraData: cmsExtraData,
      cmsDefaultLayoutData:
        mainHeader || simpleFooter || footer
          ? {
              ...(mainHeader && { header: mainHeader }),
              ...(simpleFooter && { footer: simpleFooter }),
              ...(footer && { footer }),
            }
          : undefined,
      breadcrumb,
      seoConfigBuilder: seoConfigBuilder ?? null,
      pageTitle,
      shortDescription,
      publishDate,
      pageType,
      tagsCollection,
      saleorProductSkus,
      saleorProductSkusProductVariant,
      localizationConfiguration,
    };
  } catch (error) {
    console.error('Apollo query failed or unsupported locale', error);

    return {
      cmsData: [],
      cmsExtraData: {},
      cmsDefaultLayoutData: {},
    };
  }
};

const PLACEHOLDER_PRODUCT_DETAILS = 'PlaceholderProductDetails';
const FALLBACK_COUNTRY_CODE = 'us';

const hasPlaceholderProductDetails = (items: ContentfulData[]) =>
  items.some((item) => item?.__typename === PLACEHOLDER_PRODUCT_DETAILS);

/**
 * Picks the detail page the visitor is actually served: most specific match first
 * (sku, then model, then collection), falling back to the first entry.
 */
const resolveDetailPage = <
  T extends {
    model?: string | null;
    strapSkus?: Maybe<string>[] | null;
    watchCollection?: string | null;
    watchSkus?: Maybe<string>[] | null;
  },
>(
  items: Maybe<Maybe<T>[]> | undefined,
  sku: string,
  modelName: string,
  collectionName: string,
) =>
  (sku
    ? items?.find(
        (item) =>
          item?.watchSkus?.includes(sku) || item?.strapSkus?.includes(sku),
      )
    : undefined) ??
  (modelName ? items?.find((item) => item?.model === modelName) : undefined) ??
  (collectionName
    ? items?.find((item) => item?.watchCollection === collectionName)
    : undefined) ??
  items?.[0];

/**
 * BEC-25241: a Contentful Localization configuration that omits a country stops
 * `PlaceholderProductDetails` reaching that locale, which silently hides the whole
 * PDP hero panel. Fall back to the US entry so a CMS misconfiguration cannot remove it.
 */
const withUsProductDetailsFallback = async (
  cmsData: ContentfulData[],
  client: ReturnType<typeof initializeApollo>,
  variables: ContentfulDetailPageQueryVariables,
  isPreview?: boolean | null,
): Promise<ContentfulData[]> => {
  const { collectionName, countryCode, modelName, sku } = variables;

  try {
    clearContentfulExtensions();
    const { data: rawData } = await client.query<
      ContentfulDetailPageQuery,
      ContentfulDetailPageQueryVariables
    >({
      query: DetailPageDocument,
      variables: { ...variables, countryCode: FALLBACK_COUNTRY_CODE },
      context: { client: APOLLO_CLIENTS.contentful },
    });

    const data = encodePreviewResponse(
      rawData,
      isPreview,
      getLastContentfulExtensions(),
    );

    // Prefer the page the US visitor would be served; only widen the search if
    // that page has no placeholder of its own.
    const items = data.detailPageCollection?.items;
    const usPage =
      resolveDetailPage(items, sku, modelName, collectionName)
        ?.contentCollection?.items ?? [];

    const usItems = filterContentfulCollection(
      hasPlaceholderProductDetails(filterContentfulCollection(usPage))
        ? usPage
        : items?.find((item) =>
            item?.contentCollection?.items?.some(
              (content) => content?.__typename === PLACEHOLDER_PRODUCT_DETAILS,
            ),
          )?.contentCollection?.items,
    );

    const index = usItems.findIndex(
      (item) => item?.__typename === PLACEHOLDER_PRODUCT_DETAILS,
    );

    if (index === -1) {
      captureError(
        new Error(
          `BEC-25241: no PlaceholderProductDetails for country "${countryCode}" or the US fallback; PDP hero panel will not render`,
        ),
      );
      return cmsData;
    }

    captureMessage(
      `BEC-25241: missing PlaceholderProductDetails for country "${countryCode}", served the US fallback`,
      'warning',
    );

    const withFallback = [...cmsData];
    withFallback.splice(index, 0, usItems[index]);

    return withFallback;
  } catch (error) {
    captureError(
      error instanceof Error
        ? error
        : new Error('BEC-25241: US product details fallback failed'),
    );
    return cmsData;
  }
};

export const fetchDetailPageData = async (
  type: DetailPageType,
  sku: string,
  locale: string,
  collectionName: string,
  modelName: string,
  countryCode?: string,
  isPreview?: boolean | null | undefined,
): Promise<{
  cmsData: ContentfulData[];
  cmsDefaultLayoutData?: CMSLayoutDataProps | null;
  seoConfigBuilder: SeoConfigBuilder | null;
}> => {
  const client = initializeApollo();
  collectionName =
    collectionName.charAt(0).toUpperCase() + collectionName.slice(1);

  clearContentfulExtensions();
  const detailPageVariables: ContentfulDetailPageQueryVariables = {
    type,
    sku: sku,
    modelName,
    collectionName,
    languageCode: locale,
    preview: isPreview,
    countryCode,
  };
  const { data: rawData } = await client.query<
    ContentfulDetailPageQuery,
    ContentfulDetailPageQueryVariables
  >({
    query: DetailPageDocument,
    variables: detailPageVariables,
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const detailPageCollection = resolveDetailPage(
    data.detailPageCollection?.items,
    sku,
    modelName,
    collectionName,
  );

  const seoConfigBuilder = new SeoConfigBuilder(
    data.detailPageCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.detailPageCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
    {
      productName: modelName,
      referenceNumber: sku,
    },
  );

  let filteredContentfulCollection = filterContentfulCollection(
    detailPageCollection?.contentCollection?.items,
  );

  if (
    !hasPlaceholderProductDetails(filteredContentfulCollection) &&
    countryCode !== FALLBACK_COUNTRY_CODE
  ) {
    filteredContentfulCollection = await withUsProductDetailsFallback(
      filteredContentfulCollection,
      client,
      detailPageVariables,
      isPreview,
    );
  }

  const header = data.detailPageCollection?.items?.[0]?.mainHeader;
  const footer = data.detailPageCollection?.items?.[0]?.footer;

  return {
    cmsData: filteredContentfulCollection,
    cmsDefaultLayoutData:
      header || footer
        ? {
            ...(header && { header }),
            ...(footer && { footer }),
          }
        : undefined,
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchDetailPageDataWithExtraData = async (
  type: DetailPageType,
  sku: string,
  locale: string,
  collectionName: string,
  modelName: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  localeTag?: string,
  isPreview?: boolean | null | undefined,
) => {
  const { cmsData, cmsDefaultLayoutData, seoConfigBuilder } =
    await fetchDetailPageData(
      type,
      sku,
      locale,
      collectionName,
      modelName,
      localeTag,
      isPreview,
    );
  const cmsExtraData = await fetchContentfulExtraData(
    cmsData,
    languageCode,
    channel,
    isPreview ?? undefined,
  );
  return {
    cmsData: cmsData,
    cmsExtraData: cmsExtraData,
    cmsDefaultLayoutData,
    seoConfigBuilder: seoConfigBuilder,
  };
};

export const fetchListingPageData = async (
  type:
    | 'all the watches'
    | 'all the watches filtered'
    | 'all the straps'
    | 'all the eyewear'
    | 'watch editorial page'
    | 'watch collection'
    | 'strap material',
  locale: string,
  collection?: string | null | undefined,
  editorialPageName?: string | null | undefined,
  strapMaterial?: string | null | undefined,
  countryCode?: string,
  languageCode?: LanguageCodeEnum,
  isPreview?: boolean | null | undefined,
): Promise<{
  algoliaCategoryId: string;
  algoliaRuleName: string;
  cmsData: ContentfulData[];
  cmsDefaultLayoutData?: CMSLayoutDataProps | null;
  seoConfigBuilder: SeoConfigBuilder | null;
}> => {
  const client = initializeApollo();

  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulListingPageQuery,
    ContentfulListingPageQueryVariables
  >({
    query: ListingPageDocument,
    variables: {
      preview: isPreview,
      type,
      collectionItemsLimit: 20,
      languageCode: locale,
      collectionName: collection,
      editorialPageName: editorialPageName,
      strapMaterial: strapMaterial,
      countryCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const { algoliaCategoryId, algoliaRuleName } =
    data.listingPageCollection?.items?.[0] || {};

  const seoConfigBuilder = new SeoConfigBuilder(
    data.listingPageCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.listingPageCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
    {
      collectionName: collection ?? '',
      strapMaterial: languageCode
        ? await translateStrapMaterial(languageCode, strapMaterial)
        : '',
    },
  );

  const filteredContentfulCollection = filterContentfulCollection(
    data.listingPageCollection?.items?.[0]?.contentCollection?.items,
  );

  const header = data.listingPageCollection?.items?.[0]?.mainHeader;
  const footer = data.listingPageCollection?.items?.[0]?.footer;

  return {
    algoliaCategoryId: splitEncoding(algoliaCategoryId ?? '').cleaned,
    cmsData: filteredContentfulCollection,
    cmsDefaultLayoutData:
      header || footer
        ? {
            ...(header && { header }),
            ...(footer && { footer }),
          }
        : undefined,
    algoliaRuleName: splitEncoding(algoliaRuleName ?? '').cleaned,
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchDictionary = async (
  locale: string,
  isPreview?: boolean | null | undefined,
): Promise<Array<ContentfulDictionaryItem | null>> => {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulDictionaryQuery,
    ContentfulDictionaryQueryVariables
  >({
    query: DictionaryDocument,
    variables: {
      limit: 1000,
      skip: 0,
      locale,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  return (
    (data?.dictionaryCollection?.items?.[0]?.contentCollection
      ?.items as Array<ContentfulDictionaryItem | null>) ?? []
  );
};

export const fetchSelectorModalByMarketingPartner = async (
  marketingPartner: string,
  draftMode = false,
  locale: string,
): Promise<
  | NonNullable<
      ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
    >['items'][0]
  | null
> => {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulSelectorModalByMarketingPartnerQuery,
    ContentfulSelectorModalByMarketingPartnerQueryVariables
  >({
    query: SelectorModalByMarketingPartnerDocument,
    variables: {
      marketingPartner,
      preview: draftMode,
      languageCode: locale,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    draftMode,
    getLastContentfulExtensions(),
  );

  return data?.selectorCollection?.items?.[0] ?? null;
};

export const fetchFixedPageData = async (
  slug: string,
  locale: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  countryCode?: string,
  isPreview?: boolean | null | undefined,
): Promise<{
  cms: ContentfulFixedPageCMSData;
  dictionary: ContentfulDictionaryItem[];
  seoConfigBuilder: SeoConfigBuilder;
}> => {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulFixedPageQuery,
    ContentfulFixedPageQueryVariables
  >({
    query: FixedPageDocument,
    variables: {
      slug: slug,
      languageCode: locale,
      countryCode,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const topContent = filterContentfulCollection(
    data.pageFixedCollection?.items?.[0]?.topContentCollection?.items,
  );
  const bottomContent = filterContentfulCollection(
    data.pageFixedCollection?.items?.[0]?.bottomContentCollection?.items,
  );

  const pinnedNews =
    data.pageFixedCollection?.items?.[0]?.pinnedNewsCollection?.items?.filter(
      (item): item is ContentfulPage => item !== null,
    ) ?? [];

  const pageCollectionItems = data.pageFixedCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);
  const dictionary = (data.pageFixedCollection?.items?.[0]?.dictionaryCollection
    ?.items || []) as ContentfulDictionaryItem[];

  const cmsExtraData = await fetchContentfulExtraData(
    [...topContent, ...bottomContent],
    languageCode,
    channel,
    isPreview ?? undefined,
  );

  const { saleorProductSkus, saleorProductSkusProductVariant } =
    await fetchSaleorProductSkus(
      [...topContent, ...bottomContent],
      languageCode,
      channel,
    );

  return {
    cms: {
      topContent,
      bottomContent,
      cmsExtraData,
      pinnedNews,
      saleorProductSkus,
      saleorProductSkusProductVariant,
    },
    dictionary,
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchChronologDownloadFixedPage = async (
  languageCode: ContentfulLanguageCodeEnum,
  countryCode?: string,
  isPreview?: boolean | null,
  disableErrorNotification?: boolean,
): Promise<{
  topContent: ContentfulData[];
}> => {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulFixedPageQuery,
    ContentfulFixedPageQueryVariables
  >({
    query: FixedPageDocument,
    variables: {
      slug: 'chronolog/download',
      languageCode,
      countryCode,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
      disableErrorNotification,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const topContent = filterContentfulCollection(
    data.pageFixedCollection?.items?.[0]?.topContentCollection?.items,
  );
  return {
    topContent,
  };
};

export async function getLocaleMessages(
  locale: string,
  isPreview?: boolean | null,
): Promise<LocaleMessages> {
  let data: Array<ContentfulDictionaryItem | null> = [];
  try {
    data = await fetchDictionary(locale, isPreview);
  } catch (e) {
    captureError(new Error('fetch contentful locale messages failed'));
  }
  const messages: Record<string, string> = {};
  data?.forEach((item) => {
    if (item?.key && item?.value) {
      messages[item.key] = item.value;
    }
  });

  return messages;
}

export async function fetchCMSDefaultConfig(
  locale: Locale,
  country: Country,
  isPreview?: boolean | null,
): Promise<{
  cmsDefaultLayoutData: ContentfulDefaultLayout;
  localeMessages: LocaleMessages;
}> {
  // Parallelize independent API calls for better TTFB
  const [localeMessages, cmsDefaultLayoutData] = await Promise.all([
    getLocaleMessages(locale.locale, isPreview),
    fetchCMSDefaultLayoutData(locale, country, true, isPreview).then(
      (data) => (data ?? {}) as ContentfulDefaultLayout,
    ),
  ]);

  return {
    localeMessages,
    cmsDefaultLayoutData,
  };
}

export async function fetchCMSDefaultHeaderConfig(
  locale: Locale,
  country: Country,
  isPreview?: boolean | null,
  disableErrorNotification?: boolean,
): Promise<{
  defaultHeaderSettings: ContentfulDefaultLayout;
  localeMessages: LocaleMessages;
}> {
  const client = initializeApollo();
  const contentfulLocale = locale.contentful ?? locale.locale;

  const variables: ContentfulDefaultHeaderSettingsQueryVariables = {
    languageCode: contentfulLocale,
    countryCode: country.slug,
    preview: isPreview,
  };

  const [localeMessages, headerQuery] = await Promise.all([
    getLocaleMessages(locale.locale, isPreview),
    (async () => {
      clearContentfulExtensions();
      const queryResult = await client.query<
        ContentfulDefaultHeaderSettingsQuery,
        ContentfulDefaultHeaderSettingsQueryVariables
      >({
        query: DefaultHeaderSettingsDocument,
        variables,
        context: {
          client: APOLLO_CLIENTS.contentful,
          disableErrorNotification,
        },
      });
      return {
        rawData: queryResult.data,
        extensions: getLastContentfulExtensions(),
      };
    })(),
  ]);

  const data = encodePreviewResponse(
    headerQuery.rawData,
    isPreview,
    headerQuery.extensions,
  );

  const defaultHeaderSettings = (data.defaultLayoutCollection?.items[0] ??
    {}) as ContentfulDefaultLayout;

  return {
    defaultHeaderSettings,
    localeMessages,
  };
}

type PageType = 'Homepage' | 'News' | 'Histories' | 'Legal' | 'Editorial';

export const getTypeByPageSlug = (slug: string): PageType | null => {
  if (slug === '/') {
    return 'Homepage';
  }
  // implement other page type here
  return null;
};
export const fetchCMSBoutiqueDetailData = async (
  isDefault: boolean,
  countryCode?: string,
  locale?: string | null,
  type?: BoutiqueDetailCMSTypeEnum,
  trackingKey?: string | null,
) => {
  const client = initializeApollo();
  const variables: ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables =
    isDefault ? { default: isDefault } : { trackingKey: trackingKey };
  const params =
    type === BoutiqueDetailCMSTypeEnum.Detail
      ? {
          ...variables,
          countryCode,
          locale,
          type,
        }
      : {
          default: isDefault,
          countryCode,
          locale,
          type,
          trackingKey: trackingKey,
        };
  const { data } = await client.query<
    ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQuery,
    ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables
  >({
    query: PageBoutiqueDetailCollectionBySlugAndTrackingKeyDocument,
    variables: { ...params },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const seoConfigBuilder = new SeoConfigBuilder(
    data.pageBoutiqueDetailCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.pageBoutiqueDetailCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
  );

  return {
    PageBoutiqueDetailData: { data },
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchCMSDefaultPageBoutiqueListCollectionData = async (
  selectedCountry?: string,
  isDefault?: boolean,
  isPreview?: boolean | null | undefined,
  localeTag?: string,
  locale?: string,
) => {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulPageBoutiqueListCollectionQuery,
    ContentfulPageBoutiqueListCollectionQueryVariables
  >({
    query: PageBoutiqueListCollectionQueryDocument,
    variables: {
      selectedCountry,
      isDefault,
      preview: isPreview,
      countryCode: localeTag,
      locale: locale,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  const seoConfigBuilder = new SeoConfigBuilder(
    data.pageBoutiqueListCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.pageBoutiqueListCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
  );

  return {
    PageBoutiqueListCollectionData: { data },
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export async function fetchNewsPages(
  languageCode: string,
  countryCode: string,
  isPreview?: boolean | null,
) {
  const client = initializeApollo();
  clearContentfulExtensions();
  const { data: rawData } = await client.query<
    ContentfulGetAllNewsPagesQuery,
    ContentfulGetAllNewsPagesQueryVariables
  >({
    query: GetAllNewsPagesDocument,
    variables: {
      languageCode,
      countryCode,
      preview: isPreview,
    } as ContentfulGetAllNewsPagesQueryVariables,
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.contentful,
    }),
  });

  const data = encodePreviewResponse(
    rawData,
    isPreview,
    getLastContentfulExtensions(),
  );

  return data;
}
